/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Turning a place name into coordinates.
 *
 * `event_activity_route.start_point` and friends are geo_point columns, but no
 * admin knows a start line's latitude. They know "Kanteerava Stadium". This
 * looks the name up against OpenStreetMap's Nominatim service and fills the
 * coordinates in, so typing numbers by hand becomes the fallback rather than
 * the only option.
 *
 * Deliberately a plain fetch against a public endpoint — no map library is
 * added to the admin for this, and the stored value is unchanged either way.
 */

const ENDPOINT = 'https://nominatim.openstreetmap.org/search';
const REVERSE_ENDPOINT = 'https://nominatim.openstreetmap.org/reverse';

/** Nominatim asks callers to identify themselves. */
export const GEOCODE_HEADERS = { Accept: 'application/json' };

export const MAX_SUGGESTIONS = 6;

export function geocodeUrl(query) {
  const params = new URLSearchParams({
    q: query, format: 'json', limit: String(MAX_SUGGESTIONS), addressdetails: '1',
  });
  return `${ENDPOINT}?${params.toString()}`;
}

const isNumeric = (value) => value !== '' && value !== null && value !== undefined
  && Number.isFinite(Number(value));

/**
 * The administrative parts of a match, for the fields that sit under a place.
 *
 * Requested with `addressdetails=1` because the form promises that choosing a
 * place fills in state, country and pincode — and before this it did not, so the
 * hint under the box was simply untrue.
 *
 * Nominatim names the level below a country differently around the world, and
 * none of them is guaranteed: `state` in India and the US, `province` in
 * Canada, `region` elsewhere, and `county` for places that have no state at all.
 * They are tried in order of how specific they are, and anything unmatched comes
 * back as '' so the caller writes an empty field rather than "undefined".
 */
function addressPartsOf(address) {
  if (!address || typeof address !== 'object') {
    return { state: '', country: '', pincode: '' };
  }
  const firstOf = (...keys) => {
    const found = keys.map((key) => address[key]).find((value) => value);
    return found ? String(found) : '';
  };
  return {
    state: firstOf('state', 'province', 'region', 'state_district', 'county'),
    country: firstOf('country'),
    pincode: firstOf('postcode'),
  };
}

/** Provider rows -> [{label, lat, lng, state, country, pincode}], dropping anything unusable. */
export function parseGeocodeResults(payload) {
  if (!Array.isArray(payload)) return [];
  return payload
    .filter((row) => isNumeric(row?.lat) && isNumeric(row?.lon))
    .slice(0, MAX_SUGGESTIONS)
    .map((row) => ({
      label: row.display_name || `${row.lat}, ${row.lon}`,
      lat: String(row.lat),
      lng: String(row.lon),
      ...addressPartsOf(row.address),
    }));
}

/** Looks a place up. Returns [] on any failure — search is a convenience. */
export async function geocode(query, { signal } = {}) {
  if (!query || !query.trim()) return [];
  try {
    const resp = await fetch(geocodeUrl(query.trim()), { headers: GEOCODE_HEADERS, signal });
    if (!resp.ok) return [];
    return parseGeocodeResults(await resp.json());
  } catch {
    return [];
  }
}

export function reverseGeocodeUrl(lat, lng) {
  const params = new URLSearchParams({
    lat: String(lat), lon: String(lng), format: 'json', addressdetails: '1',
  });
  return `${REVERSE_ENDPOINT}?${params.toString()}`;
}

/**
 * The opposite direction: a dropped pin -> the address it sits on.
 *
 * Needed because the map lets somebody place a start line on a road junction
 * that has no name worth searching for. Without this the address box would stay
 * whatever they last typed while the coordinates moved underneath it, and the
 * two would quietly disagree about where the race starts.
 *
 * Returns null on any failure, same contract as `geocode` returning []: the pin
 * has already moved and the coordinates are already correct, so a failed lookup
 * must leave the address alone rather than blank it.
 */
export async function reverseGeocode(lat, lng, { signal } = {}) {
  if (!isNumeric(lat) || !isNumeric(lng)) return null;
  try {
    const resp = await fetch(reverseGeocodeUrl(lat, lng), { headers: GEOCODE_HEADERS, signal });
    if (!resp.ok) return null;
    const row = await resp.json();
    if (!row || row.error || !row.display_name) return null;
    return {
      label: row.display_name,
      lat: String(lat),
      lng: String(lng),
      ...addressPartsOf(row.address),
    };
  } catch {
    return null;
  }
}
